/**
 * Expenses Page
 * Track business expenses with receipt management, filtering, and export
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  getExpenses,
  deleteExpense,
  deleteReceipt,
  getExpensesByDateRange,
  exportExpensesToCSV,
  EXPENSE_CATEGORIES,
} from '../services/expenseService';
import { formatCurrency, formatDate } from '../utils/formatters';
import ExpenseModal from '../components/ExpenseModal';
import './Expenses.css';

/**
 * Get first and last day of the current month
 */
const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
};

const Expenses = () => {
  const user = useAuthStore((state) => state.user);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [receiptViewer, setReceiptViewer] = useState(null);

  // Date range filter
  const defaultRange = getCurrentMonthRange();
  const [dateRange, setDateRange] = useState({
    start: defaultRange.start,
    end: defaultRange.end,
  });
  const [datePreset, setDatePreset] = useState('this_month');

  const loadExpenses = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      let data;
      if (dateRange.start && dateRange.end) {
        data = await getExpensesByDateRange(
          user.uid,
          new Date(dateRange.start),
          new Date(dateRange.end + 'T23:59:59'),
          categoryFilter ? { category: categoryFilter } : {}
        );
      } else {
        const filters = {};
        if (categoryFilter) filters.category = categoryFilter;
        data = await getExpenses(user.uid, filters);
      }
      setExpenses(data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  }, [user, dateRange, categoryFilter]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    let start, end;

    switch (preset) {
      case 'this_month': {
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      }
      case 'last_month': {
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      }
      case 'this_year': {
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      }
      case 'last_30': {
        start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      }
      case 'all': {
        setDateRange({ start: '', end: '' });
        return;
      }
      default:
        return;
    }

    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    });
  };

  const handleDelete = async (expense) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      // Delete receipt from storage if it exists
      if (expense.receiptUrl) {
        await deleteReceipt(expense.receiptUrl);
      }
      await deleteExpense(expense.id);
      loadExpenses();
    } catch (error) {
      alert('Error deleting expense: ' + error.message);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingExpense(null);
  };

  const handleModalSuccess = () => {
    loadExpenses();
    handleModalClose();
  };

  const handleExportCSV = () => {
    const csv = exportExpensesToCSV(filteredExpenses);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const dateStr = dateRange.start && dateRange.end
      ? `${dateRange.start}_to_${dateRange.end}`
      : 'all';
    link.href = URL.createObjectURL(blob);
    link.download = `expenses_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Filter expenses by type
  const filteredExpenses = expenses.filter(expense => {
    if (filter === 'billable') return expense.billable;
    if (filter === 'reimbursable') return expense.reimbursable;
    return true;
  });

  // Calculate totals
  const totalExpenses = filteredExpenses.reduce(
    (sum, exp) => sum + exp.amount, 0
  );
  const billableTotal = expenses
    .filter(e => e.billable)
    .reduce((sum, e) => sum + e.amount, 0);
  const reimbursableTotal = expenses
    .filter(e => e.reimbursable)
    .reduce((sum, e) => sum + e.amount, 0);

  // Category breakdown for summary
  const categoryBreakdown = {};
  filteredExpenses.forEach(exp => {
    if (!categoryBreakdown[exp.category]) {
      categoryBreakdown[exp.category] = { total: 0, count: 0 };
    }
    categoryBreakdown[exp.category].total += exp.amount;
    categoryBreakdown[exp.category].count += 1;
  });

  const sortedCategories = Object.entries(categoryBreakdown)
    .sort(([, a], [, b]) => b.total - a.total);

  // Category colors
  const getCategoryColor = (category) => {
    const colors = {
      'Travel': '#3b82f6',
      'Software & Subscriptions': '#8b5cf6',
      'Equipment': '#06b6d4',
      'Marketing & Advertising': '#10b981',
      'Office Supplies': '#f59e0b',
      'Meals & Entertainment': '#ef4444',
      'Professional Services': '#ec4899',
      'Utilities': '#14b8a6',
      'Rent': '#6366f1',
      'Insurance': '#a855f7',
      'Taxes & Licenses': '#f97316',
      'Bank Fees': '#64748b',
      'Education & Training': '#0ea5e9',
      'Other': '#6b7280',
    };
    return colors[category] || '#6b7280';
  };

  const getCategoryBadge = (category) => (
    <span style={{
      padding: '0.25rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
      fontWeight: '500',
      backgroundColor: getCategoryColor(category),
      color: 'white',
    }}>
      {category}
    </span>
  );

  // Receipt thumbnail
  const getReceiptThumb = (expense) => {
    if (!expense.receiptUrl) return null;
    return (
      <button
        className="receipt-thumb"
        onClick={() => setReceiptViewer(expense.receiptUrl)}
        title="View receipt"
        type="button"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </button>
    );
  };

  if (loading && expenses.length === 0) {
    return <div className="loading-state">Loading expenses...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Expenses</h1>
          <p className="page-subtitle">
            Track daily business expenses and receipts
          </p>
        </div>
        <div className="page-header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleExportCSV}
            disabled={filteredExpenses.length === 0}
          >
            Export CSV
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            + Add Expense
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fee2e2' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Expenses</div>
            <div className="stat-value">{formatCurrency(totalExpenses)}</div>
            <div className="stat-meta">{filteredExpenses.length} expenses</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#dbeafe' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Billable</div>
            <div className="stat-value">{formatCurrency(billableTotal)}</div>
            <div className="stat-meta">Can be billed to clients</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fef3c7' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-label">Reimbursable</div>
            <div className="stat-value">
              {formatCurrency(reimbursableTotal)}
            </div>
            <div className="stat-meta">To be reimbursed</div>
          </div>
        </div>
      </div>

      {/* Date Range & Filters */}
      <div className="filters-bar">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="date-preset">Period:</label>
            <select
              id="date-preset"
              className="input input-small"
              value={datePreset}
              onChange={(e) => handleDatePreset(e.target.value)}
            >
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="last_30">Last 30 Days</option>
              <option value="this_year">This Year</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {datePreset === 'custom' && (
            <>
              <div className="filter-group">
                <label htmlFor="date-start">From:</label>
                <input
                  id="date-start"
                  type="date"
                  className="input input-small"
                  value={dateRange.start}
                  onChange={(e) => {
                    setDateRange(prev => ({
                      ...prev,
                      start: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="filter-group">
                <label htmlFor="date-end">To:</label>
                <input
                  id="date-end"
                  type="date"
                  className="input input-small"
                  value={dateRange.end}
                  onChange={(e) => {
                    setDateRange(prev => ({
                      ...prev,
                      end: e.target.value,
                    }));
                  }}
                />
              </div>
            </>
          )}

          <div className="filter-group">
            <label htmlFor="category-filter">Category:</label>
            <select
              id="category-filter"
              className="input input-small"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {EXPENSE_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="type-filter">Type:</label>
            <select
              id="type-filter"
              className="input input-small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Expenses</option>
              <option value="billable">Billable Only</option>
              <option value="reimbursable">Reimbursable Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Monthly Category Summary */}
      {sortedCategories.length > 0 && (
        <div className="category-summary">
          <h3 className="category-summary-title">
            Category Breakdown
          </h3>
          <div className="category-bars">
            {sortedCategories.map(([category, data]) => {
              const percentage = totalExpenses > 0
                ? (data.total / totalExpenses) * 100
                : 0;
              return (
                <div key={category} className="category-bar-item">
                  <div className="category-bar-header">
                    <span className="category-bar-label">
                      {category}
                      <span className="category-bar-count">
                        ({data.count})
                      </span>
                    </span>
                    <span className="category-bar-amount">
                      {formatCurrency(data.total)}
                    </span>
                  </div>
                  <div className="category-bar-track">
                    <div
                      className="category-bar-fill"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: getCategoryColor(category),
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Expenses Table */}
      <div className="card">
        {filteredExpenses.length === 0 ? (
          <div className="empty-state">
            <p>No expenses found for this period</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowModal(true)}
            >
              Add Your First Expense
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                  <th>Receipt</th>
                  <th>Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map(expense => (
                  <tr key={expense.id}>
                    <td className="nowrap">
                      {formatDate(expense.date)}
                    </td>
                    <td>{getCategoryBadge(expense.category)}</td>
                    <td>{expense.description}</td>
                    <td className="text-secondary">
                      {expense.vendor || '-'}
                    </td>
                    <td style={{ fontWeight: '600' }}>
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="receipt-cell">
                      {getReceiptThumb(expense)}
                    </td>
                    <td>
                      {expense.billable && (
                        <span className="badge badge-info">
                          Billable
                        </span>
                      )}
                      {expense.reimbursable && (
                        <span className="badge badge-warning">
                          Reimbursable
                        </span>
                      )}
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-icon"
                        onClick={() => handleEdit(expense)}
                        title="Edit"
                        aria-label="Edit expense"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className="btn-icon btn-icon-danger"
                        onClick={() => handleDelete(expense)}
                        title="Delete"
                        aria-label="Delete expense"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Expense Modal */}
      {showModal && (
        <ExpenseModal
          expense={editingExpense}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Receipt Viewer Modal */}
      {receiptViewer && (
        <div
          className="modal-overlay"
          onClick={() => setReceiptViewer(null)}
        >
          <div
            className="receipt-viewer"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="receipt-viewer-close"
              onClick={() => setReceiptViewer(null)}
              aria-label="Close receipt viewer"
            >
              &#x2715;
            </button>
            {receiptViewer.endsWith('.pdf') ? (
              <iframe
                src={receiptViewer}
                title="Receipt PDF"
                className="receipt-viewer-iframe"
              />
            ) : (
              <img
                src={receiptViewer}
                alt="Receipt"
                className="receipt-viewer-img"
              />
            )}
            <a
              href={receiptViewer}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary receipt-viewer-download"
            >
              Open in New Tab
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
